import React, { useEffect, useRef, useState } from 'react';
import { draftPlan, fetchSource, planChat, savePlan, uploadSource } from '../api/gameBuilderAPI';
import './NewPlan.css';

/**
 * Steps 1-2 of the Game Builder (backend services/game_builder/planner.py):
 *   1. a SOURCE - upload a document (.md .txt .pdf), give a web page address, or just chat
 *   2. a short CHAT - only what the source can't tell (or, with no source, the job itself)
 *   3. DRAFT the plan (one LLM call) -> the creator reviews goal, roles, tasks and missions -> SAVE
 * The saved plan appears in the Game Builder's plan list, ready to build its missions.
 */
const SOURCES = [
  { id: 'upload', icon: '📄', label: 'Upload a document' },
  { id: 'url', icon: '🔗', label: 'A web page' },
  { id: 'chat', icon: '💬', label: 'Just chat' }
];

export default function NewPlan({ onSaved, onCancel }) {
  const [sourceKind, setSourceKind] = useState('upload');
  const [document, setDocument] = useState(null);      // { name, title, chars, truncated, preview }
  const [url, setUrl] = useState('');
  const [messages, setMessages] = useState([]);
  const [ready, setReady] = useState(false);
  const [draft, setDraft] = useState(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState('');                // what's running, shown to the creator
  const [error, setError] = useState('');
  const chatEnd = useRef(null);
  const chatting = sourceKind === 'chat' || !!document;

  useEffect(() => { chatEnd.current?.scrollIntoView({ block: 'nearest' }); }, [messages]);

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  // The opening question is static on the backend (no LLM call).
  const startChat = async (doc) => {
    const out = await run('Starting…', () => planChat(doc?.name || null, []));
    if (out) setMessages([{ role: 'assistant', content: out.reply }]);
  };

  const chooseSource = (id) => {
    setSourceKind(id);
    setDocument(null);
    setMessages([]);
    setDraft(null);
    setReady(false);
    setError('');
    if (id === 'chat') startChat(null);
  };

  const upload = async (file) => {
    if (!file) return;
    const out = await run('Reading the document…', () => uploadSource(file));
    if (out) { setDocument(out.document); startChat(out.document); }
  };

  const fetchUrl = async () => {
    const out = await run('Fetching the page…', () => fetchSource(url));
    if (out) { setDocument(out.document); startChat(out.document); }
  };

  const send = async () => {
    const text = answer.trim();
    if (!text) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setAnswer('');
    const out = await run('Thinking…', () => planChat(document?.name || null, next));
    if (out) {
      setMessages([...next, { role: 'assistant', content: out.reply }]);
      setReady(out.ready);
    }
  };

  const makeDraft = async () => {
    const out = await run('Drafting the missions… (about 20 seconds)', () => draftPlan(document?.name || null, messages));
    if (out) setDraft(out.plan);
  };

  const save = async () => {
    const out = await run('Saving…', () => savePlan(draft));
    if (out) onSaved(out.id);
  };

  const answered = messages.some((m) => m.role === 'user');
  const canDraft = !busy && (document || answered);

  return (
    <section className="gb-card np" data-tour="np">
      <header className="np-head">
        <h2>New plan</h2>
        <button type="button" className="gb-btn gb-btn-quiet" onClick={onCancel}>Cancel</button>
      </header>

      {!draft && (
        <>
          <div className="np-sources" role="tablist" data-tour="np-sources">
            {SOURCES.map((s) => (
              <button key={s.id} type="button" role="tab" aria-selected={sourceKind === s.id}
                className={`np-source ${sourceKind === s.id ? 'is-active' : ''}`} onClick={() => chooseSource(s.id)} disabled={!!busy}>
                <span>{s.icon}</span>{s.label}
              </button>
            ))}
          </div>

          {sourceKind === 'upload' && !document && (
            <label className="np-drop">
              <input type="file" accept=".md,.txt,.pdf" onChange={(e) => upload(e.target.files?.[0])} disabled={!!busy} />
              <span>Choose a .pdf, .md or .txt file (up to 5 MB) - an SOP, manual, policy or lesson.</span>
            </label>
          )}
          {sourceKind === 'url' && !document && (
            <form className="np-url" onSubmit={(e) => { e.preventDefault(); fetchUrl(); }}>
              <input type="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} disabled={!!busy} />
              <button type="submit" className="gb-btn" disabled={!url.trim() || !!busy}>Fetch</button>
            </form>
          )}
          {document && (
            <div className="np-doc">
              <strong>📄 {document.title}</strong>
              <span className="gb-muted gb-small">{document.chars.toLocaleString()} characters{document.truncated ? ' (cut to the first part)' : ''}</span>
              <p className="gb-muted gb-small">{document.preview}…</p>
            </div>
          )}

          {chatting && messages.length > 0 && (
            <div className="np-chat" data-tour="np-chat">
              {messages.map((m, i) => <div key={i} className={`np-msg is-${m.role}`}>{m.content}</div>)}
              <div ref={chatEnd} />
              <form className="np-answer" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <textarea rows={2} value={answer} placeholder="Your answer…" disabled={!!busy}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <button type="submit" className="gb-btn gb-btn-quiet" disabled={!answer.trim() || !!busy}>Send</button>
              </form>
            </div>
          )}

          {chatting && (
            <div className="gb-actions">
              <button type="button" className="gb-btn" data-tour="np-draft" disabled={!canDraft} onClick={makeDraft}>Draft the missions</button>
              <span className="gb-muted gb-small">
                {ready ? 'Ready when you are.' : document ? 'Answering a question or two makes the missions fit your people better.' : 'Answer a few questions first.'}
              </span>
            </div>
          )}
        </>
      )}

      {busy && <p className="gb-muted"><span className="gb-spinner" /> {busy}</p>}
      {error && <div className="gb-error" role="alert">{error}</div>}

      {draft && (
        <div className="np-draft">
          <p className="gb-muted">This is what the missions will train. Save it, or go back and tell the chat what to change.</p>
          <dl className="gb-facts">
            <div><dt>Goal</dt><dd>{draft.goal}</dd></div>
            <div><dt>Player</dt><dd>{draft.player_role}</dd></div>
            <div><dt>Others</dt><dd>{draft.other_roles.join(', ') || '—'}</dd></div>
            <div><dt>Who plays</dt><dd>{draft.learners}</dd></div>
            <div><dt>Setting</dt><dd>{draft.setting || '—'}</dd></div>
          </dl>
          <h3>Tasks</h3>
          {draft.tasks.map((t) => (
            <div key={t.id} className="np-task">
              <strong>{t.id} · {t.title}</strong>
              {t.rules.length > 0 && <ul>{t.rules.map((r) => <li key={r}>{r}</li>)}</ul>}
              <div className="gb-small">{t.mistakes.map((m) => <div key={m.mistake}>⚠️ {m.mistake} → {m.consequence}</div>)}</div>
            </div>
          ))}
          <h3>Missions</h3>
          {draft.missions.map((m) => (
            <div key={m.id} className="np-task">
              <strong>{m.id} · {m.name}</strong> <span className="gb-tag">{m.template_name}</span>
              <p className="gb-small"><strong>Objective:</strong> {m.objective}</p>
              <p className="gb-small gb-muted"><strong>Walks into:</strong> {m.scenario}</p>
              <p className="gb-small gb-muted"><strong>Traps:</strong> {m.traps.join(' · ')}</p>
            </div>
          ))}
          <div className="gb-actions">
            <button type="button" className="gb-btn" data-tour="np-save" onClick={save} disabled={!!busy}>Save plan</button>
            <button type="button" className="gb-btn gb-btn-quiet" disabled={!!busy}
              onClick={() => { setDraft(null); setMessages((ms) => [...ms, { role: 'assistant', content: 'What should change in the missions?' }]); }}>
              Change something
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
